
import React, {useState} from "react";
import { Button, Table, Tooltip } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { dateFormat, formatToCompanyTimezone } from "../../../../lib/functions";
import { useNavigate } from "react-router-dom";
import "../../../../components/common/bt.css";

const fmtMoney = (val) =>
  Number(val || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const buildWithDetailsRows = (data) => {
  const rows = [];
  data.forEach((voucher) => {
    const details = voucher.details || [];
    const subtotal = details.reduce((sum, d) => sum + Number(d.rcv_total || 0), 0);

    details.forEach((d, dIdx) => {
      rows.push({
        key: `${voucher.id}-${dIdx}`,
        isSubtotal: false,
        isFirstDetail: dIdx === 0,
        voucher,
        detail: d,
      });
    });

    rows.push({ key: `subtotal-${voucher.id}`, isSubtotal: true, subtotal });
  });
  return rows;
};

const DebtorReceiptRecordTable = ({ printMode = false }) => {
  const { list, loading, filters } = useSelector(
    (state) => state.debtorReceiptRecord
  );

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const navigate = useNavigate();

  const voucherColumn = {
    title: "Voucher",
    dataIndex: "voucher",
    align: "center",
    width: 80,
    render: (_, record) => (
      <Tooltip title="View Receipt Voucher">
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() =>
            navigate(`/accounts/debitor-receipts/view/${record.id}`)
          }
        />
      </Tooltip>
    ),
  };


  // Columns for without details view
  const withoutDetailsColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "VCH No", dataIndex: "rcv_code" },
    {
      title: "Date",
      dataIndex: "created_date",
      render: (v) =>
    v ? formatToCompanyTimezone(v).split(" ")[0] : "-",
    },
    { title: "Code", dataIndex: "acc_code" },
    { title: "Customer Name", dataIndex: "acc_name" },
    { title: "Mobile No", dataIndex: "contact_no" },
    { title: "Address", dataIndex: "address" },
    { title: "Receipt By", dataIndex: "full_name" },
    {
      title: "Total Amount",
      dataIndex: "rcv_total",
      render: (val) =>
        Number(val || 0).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
      align: "right",
    },
    ...(printMode ? [] : [voucherColumn]),
  ];

  // Columns for child detail rows
  const detailColumns = [
    { title: "Sl No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "Voucher No", dataIndex: "voucher_no" },
    { title: "Receipt Into", dataIndex: "into_acc_name" },
    { title: "Amount", dataIndex: "rcv_amount", align: "right" },
  ];

  // Columns for with details view
  const withDetailsColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "VCH", dataIndex: "rcv_code" },
    {
      title: "Date",
      dataIndex: "created_date",
      render: (v) =>
    v ? formatToCompanyTimezone(v).split(" ")[0] : "-",
    },
    { title: "Code", dataIndex: "acc_code" },
    { title: "Customer", dataIndex: "acc_name" },
    { title: "Mobile No", dataIndex: "contact_no" },
    { title: "Address", dataIndex: "address" },
    { title: "Receipt By", dataIndex: "full_name" },
    ...(printMode ? [] : [voucherColumn]),
    // { title: "Receipt Into", dataIndex: "into_acc_name" },
    // {
    //   title: "Receipt Amount",
    //   dataIndex: "rcv_total",
    //   render: (val) =>
    //     Number(val || 0).toLocaleString(undefined, {
    //       minimumFractionDigits: 2,
    //       maximumFractionDigits: 2,
    //     }),
    //   align: "right",
    // },
  ];

  const columns =
    filters.recordType === "with details"
      ? withDetailsColumns
      : withoutDetailsColumns;

  // Calculate grand totals
  // const totals = list.reduce(
  //   (acc, row) => {
  //     acc.total += Number(row.rcv_total || 0);
  //     return acc;
  //   },
  //   { total: 0 }
  // );
  const totals = list.reduce(
  (acc, row) => {
    if (filters.recordType === "with details") {
      const detailSum = (row.details || []).reduce(
        (sum, d) => sum + Number(d.rcv_total || 0),
        0
      );
      acc.total += detailSum;
    } else {
      acc.total += Number(row.rcv_total || 0);
    }
    return acc;
  },
  { total: 0 }
);

  if (printMode) {
    const isWithDetails = filters.recordType === "with details";

    if (isWithDetails) {
      const rows = buildWithDetailsRows(list);
      return (
        <table className="ledger-print-table">
          <colgroup>
            <col style={{ width: "3%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "7%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "17%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "10%" }} />
          </colgroup>
          <tbody>
            <tr className="ledger-print-heading">
              <td>No.</td>
              <td>VCH</td>
              <td>Date</td>
              <td>Code</td>
              <td>Customer</td>
              <td>Mobile No</td>
              <td>Address</td>
              <td>Receipt By</td>
              <td>Receipt Into</td>
              <td style={{ textAlign: "right" }}>Amount</td>
            </tr>

            {rows.map((row, index) =>
              row.isSubtotal ? (
                <tr className="ledger-opening-row" key={row.key}>
                  <td colSpan={9} style={{ textAlign: "right" }}>
                    Sub Total
                  </td>
                  <td style={{ textAlign: "right" }}>{fmtMoney(row.subtotal)}</td>
                </tr>
              ) : (
                <tr key={row.key}>
                  <td>{row.isFirstDetail ? index + 1 : ""}</td>
                  <td>{row.isFirstDetail ? row.voucher.rcv_code : ""}</td>
                  <td>
                    {row.isFirstDetail
                      ? row.voucher.created_date
                        ? formatToCompanyTimezone(row.voucher.created_date).split(" ")[0]
                        : "-"
                      : ""}
                  </td>
                  <td>{row.isFirstDetail ? row.voucher.acc_code : ""}</td>
                  <td>{row.isFirstDetail ? row.voucher.acc_name : ""}</td>
                  <td  style={{
    maxWidth: "80px",
    whiteSpace: "normal",
    wordBreak: "break-word",
  }}>{row.isFirstDetail ? row.voucher.contact_no : ""}</td>
                  <td  style={{
    maxWidth: "80px",
    whiteSpace: "normal",
    wordBreak: "break-word",
  }}>{row.isFirstDetail ? row.voucher.address : ""}</td>
                  <td>{row.isFirstDetail ? row.voucher.full_name : ""}</td>
                  <td>{row.detail.into_acc_name}</td>
                  <td style={{ textAlign: "right" }}>{fmtMoney(row.detail.rcv_total)}</td>
                </tr>
              )
            )}

            <tr className="ledger-total-row">
              <td colSpan={9} style={{ textAlign: "right" }}>
                Grand Total
              </td>
              <td style={{ textAlign: "right" }}>{fmtMoney(totals.total)}</td>
            </tr>
          </tbody>
        </table>
      );
    }

    return (
      <table className="ledger-print-table">
        <colgroup>
          <col style={{ width: "4%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: "16%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "19%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "13%" }} />
        </colgroup>
        <tbody>
          <tr className="ledger-print-heading">
            <td>No.</td>
            <td>VCH No</td>
            <td>Date</td>
            <td>Code</td>
            <td>Customer Name</td>
            <td>Mobile No</td>
            <td>Address</td>
            <td>Receipt By</td>
            <td style={{ textAlign: "right" }}>Total Amount</td>
          </tr>

          {list.map((row, index) => (
            <tr key={row.id || index}>
              <td>{index + 1}</td>
              <td>{row.rcv_code}</td>
              <td>{row.created_date ? formatToCompanyTimezone(row.created_date).split(" ")[0] : "-"}</td>
              <td>{row.acc_code}</td>
              <td>{row.acc_name}</td>
              <td style={{
    maxWidth: "80px",
    whiteSpace: "normal",
    wordBreak: "break-word",
  }}>{row.contact_no}</td>
              <td style={{
    maxWidth: "80px",
    whiteSpace: "normal",
    wordBreak: "break-word",
  }}>{row.address}</td>
              <td  style={{
    maxWidth: "80px",
    whiteSpace: "normal",
    wordBreak: "break-word",
  }}>{row.full_name}</td>
              <td style={{ textAlign: "right" }}>{fmtMoney(row.rcv_total)}</td>
            </tr>
          ))}

          <tr className="ledger-total-row">
            <td colSpan={8} style={{ textAlign: "right" }}>
              Grand Total
            </td>
            <td style={{ textAlign: "right" }}>{fmtMoney(totals.total)}</td>
          </tr>
        </tbody>
      </table>
    );
  }

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={list}
      loading={loading}
      pagination={{
        ...pagination,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50"],
      }}
      onChange={handleTableChange}
      bordered
      scroll={printMode ? undefined : { x: "max-content" }}
      size={printMode ? "small" : "middle"}
      expandable={
        filters.recordType === "with details"
          ? {
              expandedRowRender: (record) => {
                const subtotal = record.details?.reduce(
                  (sum, d) => sum + Number(d.rcv_total || 0),
                  0
                );
                return (
                  <>
                    <Table
                      rowKey="detailId"
                      columns={detailColumns}
                      dataSource={record.details || []}
                      pagination={false}
                      size="small"
                      bordered
                      summary={() => (
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={3} align="right">
                            <strong>Sub Total:</strong>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2} align="right">
                            <strong>{subtotal?.toFixed(2)}</strong>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      )}
                    />
                  </>
                );
              },
              rowExpandable: (record) => record.details?.length > 0,
            }
          : undefined
      }
      summary={() => {
        const colSpan = filters.recordType === "with details" ? 8 : 8;
        return (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={colSpan} align="right">
              <strong>Grand Total:</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="right">
              <strong>
                {totals.total.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </strong>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        );
      }}
    />
  );
};

export default DebtorReceiptRecordTable;
